"use client";

import type { ReactNode } from "react";
import type { CoverageState } from "./coverage";
import type { MapLinkComponent } from "./link";
import {
  type StepPalette,
  type StepSegment,
  StepSegmentBar,
} from "./StepSegmentBar";

export interface WorkflowCardProps<S extends string = CoverageState> {
  name: string;
  description?: string | null;
  /** The workflow's page; the whole card links there. */
  href: string;
  steps: StepSegment<S>[];
  /** The steps' colors and names; the map's by default. */
  palette?: StepPalette<S>;
  /**
   * What the card's top right says. By default, the map's counts of steps
   * with multiple applications and steps not mapped.
   */
  meta?: ReactNode;
  /** The longest strip among the cards stacked with this one, so they align. */
  slots?: number;
  /** Called when a step pill is clicked, e.g. to open the workflow at it. */
  onStepSelect?: (stepId: string) => void;
  /** Extra notes under the strip. */
  footer?: ReactNode;
  linkComponent?: MapLinkComponent;
}

function count(n: number, singular: string, pluralForm: string) {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

/**
 * A workflow as a card in a function's list: its name, how many steps have
 * several applications or none, its description, and its steps as a strip of
 * fixed-width pills that lines up with the cards around it. The whole card
 * opens the workflow; each pill shows its step on hover.
 */
export function WorkflowCard<S extends string = CoverageState>({
  name,
  description,
  href,
  steps,
  palette,
  meta,
  slots,
  onStepSelect,
  footer,
  linkComponent: Link = "a",
}: WorkflowCardProps<S>) {
  const multiple = steps.filter(
    (step) => (step.coverage as string) === "multiple",
  ).length;
  const none = steps.filter((step) => step.coverage === "none").length;
  return (
    <article className="relative rounded-(--radius-container) border border-(--border-subtle) bg-(--bg-white) p-5 transition-colors focus-within:border-(--border-strong) hover:border-(--border-strong)">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold">
          {/* Stretched link: the whole card opens the workflow. */}
          <Link
            href={href}
            className="no-underline! text-inherit! after:absolute after:inset-0 after:rounded-(--radius-container) focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-(--fg-primary)"
          >
            {name}
          </Link>
        </h3>
        <p className="flex items-center gap-3 text-[11px] tabular-nums text-(--fg-muted)!">
          {meta ?? (
            <>
              {multiple > 0 && (
                <span>
                  {count(
                    multiple,
                    "step with multiple applications",
                    "steps with multiple applications",
                  )}
                </span>
              )}
              {none > 0 && <span>{none} not mapped</span>}
            </>
          )}
        </p>
      </div>
      {description && (
        <p className="mt-1! max-w-2xl text-[12px] leading-relaxed text-(--fg-secondary)!">
          {description}
        </p>
      )}
      {/* Above the stretched link, so each pill keeps its own hover. */}
      <div className="relative z-10 mt-3 w-fit max-w-full">
        {steps.length > 0 ? (
          <StepSegmentBar
            steps={steps}
            palette={palette}
            label={`Steps in ${name}`}
            layout="fixed"
            slots={slots}
            onStepSelect={onStepSelect}
            selectHint={onStepSelect ? "Open workflow →" : undefined}
          />
        ) : (
          <p className="text-[12px] text-(--fg-muted)!">
            No steps defined yet.
          </p>
        )}
      </div>
      {footer && <div className="mt-3">{footer}</div>}
    </article>
  );
}
